/**
 * Theme preference: follow the operating system, or force light or dark.
 *
 * BUILD_PLAN.md section 11.0: the default follows the OS, and the choice is
 * remembered per user. The API keeps each user's choice, and this browser's
 * localStorage caches it for the first paint (ADR 0041).
 *
 * public/theme-init.js repeats readPreference/resolveTheme in plain JavaScript
 * so the right theme is applied before first paint. Keep the two in step;
 * tests/theme-init.test.ts runs both against the same cases.
 */

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "axon-time.theme";
export const DARK_QUERY = "(prefers-color-scheme: dark)";

const PREFERENCES: readonly ThemePreference[] = ["system", "light", "dark"];

function isPreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && (PREFERENCES as readonly string[]).includes(value);
}

/** The stored preference, or "system". Storage can be absent or throw (private windows). */
export function readPreference(storage: Storage | undefined): ThemePreference {
  try {
    const value = storage?.getItem(THEME_STORAGE_KEY);
    return isPreference(value) ? value : "system";
  } catch {
    return "system";
  }
}

/** Remember the preference. "system" is the default, so it clears the stored value. */
export function writePreference(storage: Storage | undefined, preference: ThemePreference): void {
  try {
    if (preference === "system") {
      storage?.removeItem(THEME_STORAGE_KEY);
    } else {
      storage?.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    // Not remembered; the choice still applies for this page view.
  }
}

export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference === "system") {
    return systemPrefersDark ? "dark" : "light";
  }
  return preference;
}

export function applyTheme(root: HTMLElement, theme: ResolvedTheme): void {
  root.dataset.theme = theme;
}
